import React, { useState } from 'react'

import { SxProps } from '@mui/material'

import type { TAnalysisTypeDto, TAnalysisTypeStatus } from '@homepal/api-contract'

import { richTextToPlainText } from 'utils/richText'
import { isMissingConfiguration } from 'utils/analysis'
import { useAnalysisTypes } from 'redux/hooks/AnalysisTypes'
import { useAnalysisTypeAnalyses } from 'redux/hooks/Analyses'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'
import Table from 'components_new/atoms/Table'
import TableRow from 'components_new/atoms/Table/TableRow'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableHead from 'components_new/atoms/Table/TableHead'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GenericListItem from 'components_new/molecules/GenericListItem'
import LoadingSection from 'components_new/molecules/LoadingSection'
import SearchField from 'components_new/molecules/SearchField'
import StatusBox from 'components_new/molecules/StatusBox'

import { DISABLED_SX_PROPS } from 'themes'
import { Highlighted } from '../../StandardSearch/utils'

interface AnalysisTypesTableProps {
  status: TAnalysisTypeStatus | null
  sx?: SxProps
}

const AnalysisTypeRow = (props: {
  analysisType: TAnalysisTypeDto
  searchQuery: string
}) => {
  const { analysisType, searchQuery } = props

  const { analyses, loading: analysesLoading } = useAnalysisTypeAnalyses(
    analysisType.id
  )
  const { t, translateEnum } = useTranslation()

  const status = translateEnum('analysis_type_status', analysisType.status)

  return (
    <TableRow
      href={`/analysis-types/${analysisType.id}`}
      hover={true}
      sx={
        isMissingConfiguration(analysisType)
          ? { ...DISABLED_SX_PROPS, cursor: 'pointer', pointerEvents: 'auto' }
          : undefined
      }
    >
      <TableCell>
        <GenericListItem
          body={richTextToPlainText(analysisType.description)}
          customTitle={
            <Highlighted text={analysisType.name} highlight={searchQuery} />
          }
          disablePadding={true}
          sx={{
            maxWidth: { xs: 256, lg: 512, xl: 768 }
          }}
          title={analysisType.name}
          variant="analysis_type"
        />
      </TableCell>
      <TableCell>
        <Box
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {analysesLoading ? null : analyses.length === 0 ? (
            <>{t('analysis_types', 'no_runs')}</>
          ) : (
            <Link href={`/analysis-types/${analysisType.id}/analyses`}>
              {analyses.length}{' '}
              {t(
                'analyses',
                analyses.length === 1 ? 'analysis' : 'analyses',
                true
              )}
            </Link>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Chip color={status.color} label={status.title} size="xsmall" />
      </TableCell>
      <TableCell padding="checkbox">
        <Icon fontSize="small" name="ChevronRight" />
      </TableCell>
    </TableRow>
  )
}

const AnalysisTypesTable = (props: AnalysisTypesTableProps) => {
  const { status, sx } = props

  const { error, loading, analysisTypes } = useAnalysisTypes()
  const { t } = useTranslation()

  const [searchQuery, setSearchQuery] = useState('')

  const filteredAnalysisTypes = analysisTypes.filter((analysisType) =>
    analysisType.name.toLowerCase().includes(searchQuery.toLowerCase())
  )
  const displayedAnalysisTypes = filteredAnalysisTypes.filter(
    (item) => !status || item.status === status
  )

  if (loading) {
    return (
      <Box sx={sx}>
        <LoadingSection sx={{ p: 2 }} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={sx}>
        <StatusBox
          allowClose={false}
          severity="error"
          title={t('analysis_types', 'error_load_title')}
        >
          {t('analysis_types', 'error_load_body')}
        </StatusBox>
      </Box>
    )
  }

  if (analysisTypes.length === 0) {
    return (
      <Box sx={sx}>
        <EmptyPlaceholder
          icon="Analysis"
          size="small"
          title={t('analysis_types', 'no_analysis_types_title')}
        />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...sx
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
        <SearchField
          onClear={() => setSearchQuery('')}
          onSearch={(value) => setSearchQuery(value)}
          placeholder={t('analysis_types', 'search_placeholder')}
          size="small"
          value={searchQuery}
        />
      </Box>
      <Text color="text.disabled" variant="caption">
        {t('global', 'shows')} <b>{displayedAnalysisTypes.length}</b>{' '}
        {t('global', 'of', true)} {analysisTypes.length}{' '}
        {t('analysis_types', 'analysis_types', true)}
      </Text>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell size="medium" sx={{ width: '70%' }}>
                {t('analysis_types', 'analysis_type')}
              </TableCell>
              <TableCell size="medium" sx={{ width: '15%' }}>
                {t('analyses', 'analyses')}
              </TableCell>
              <TableCell size="medium" sx={{ width: '15%' }}>
                {t('global', 'status')}
              </TableCell>
              <TableCell padding="checkbox" size="medium">
                {''}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedAnalysisTypes.map((item) => (
              <AnalysisTypeRow
                analysisType={item}
                key={item.id}
                searchQuery={searchQuery}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}

export default AnalysisTypesTable
